import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import LoginPanel from '../Auth/LoginPanel';
import { adminApi } from '../../services/adminApi';

const AdminPortal = () => {
  const { isAuthenticated, user, token, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    quota_per_day: 250,
    role: 'user',
  });
  const [creating, setCreating] = useState(false);

  const usageMap = useMemo(() => {
    const map = new Map();
    usage.forEach((row) => {
      map.set(row.id, row);
    });
    return map;
  }, [usage]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [usersRes, sessionsRes, usageRes] = await Promise.all([
          adminApi.listUsers(token),
          adminApi.listSessions(token),
          adminApi.getUsage(token),
        ]);
        setUsers(usersRes.users || []);
        setSessions(sessionsRes.sessions || []);
        setUsage(usageRes.usage || []);
      } catch (err) {
        setError(err.message || 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, token]);

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError('');
    try {
      await adminApi.createUser(
        {
          email: createForm.email,
          password: createForm.password,
          quota_per_day: Number(createForm.quota_per_day),
          role: createForm.role,
        },
        token
      );
      setCreateForm({
        email: '',
        password: '',
        quota_per_day: 250,
        role: 'user',
      });
      const usersRes = await adminApi.listUsers(token);
      setUsers(usersRes.users || []);
    } catch (err) {
      setError(err.message || 'Unable to create user');
    } finally {
      setCreating(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ py: 8 }}>
        <LoginPanel
          title="Admin Portal Login"
          subtitle="Only authorized users can access administrative tools."
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Admin Portal
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users, monitor sessions, and enforce daily search limits.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Logged in as ${user?.email}`} color="primary" />
          <Button variant="outlined" color="inherit" onClick={logout}>
            Logout
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Usage Summary (Today)
                  </Typography>
                  <Stack spacing={2}>
                    {users.map((u) => {
                      const row = usageMap.get(u.id);
                      return (
                        <Box
                          key={u.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Box>
                            <Typography fontWeight={600}>{u.email}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Quota: {u.quota_per_day} / day
                            </Typography>
                          </Box>
                          <Chip
                            color={
                              row && row.today_usage >= u.quota_per_day
                                ? 'error'
                                : 'success'
                            }
                            label={`${row?.today_usage ?? 0} used`}
                          />
                        </Box>
                      );
                    })}
                    {users.length === 0 && (
                      <Typography color="text.secondary">
                        No users available. Create one below.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card component="form" onSubmit={handleCreateUser}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Create User
                  </Typography>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    fullWidth
                    margin="dense"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    required
                  />
                  <TextField
                    label="Password"
                    name="password"
                    type="password"
                    fullWidth
                    margin="dense"
                    value={createForm.password}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    required
                  />
                  <TextField
                    label="Daily Search Quota"
                    name="quota_per_day"
                    type="number"
                    fullWidth
                    margin="dense"
                    value={createForm.quota_per_day}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        quota_per_day: e.target.value,
                      }))
                    }
                    required
                  />
                  <TextField
                    label="Role"
                    name="role"
                    fullWidth
                    margin="dense"
                    value={createForm.role}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        role: e.target.value,
                      }))
                    }
                    helperText='Use "admin" for elevated access'
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ mt: 2 }}
                    disabled={creating}
                  >
                    {creating ? 'Creating...' : 'Create User'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Active Sessions
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                {sessions.length === 0 && (
                  <Typography color="text.secondary">
                    No active sessions detected.
                  </Typography>
                )}
                {sessions.map((session) => (
                  <Box
                    key={session.id}
                    sx={{
                      border: '1px solid',
                      borderColor: session.suspicious ? 'error.main' : 'divider',
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box>
                        <Typography fontWeight={600}>
                          {session.email} ({session.role})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Device: {session.user_agent || 'Unknown'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={`Last seen ${new Date(
                            session.last_seen
                          ).toLocaleString()}`}
                          color="info"
                          variant="outlined"
                        />
                        {session.suspicious ? (
                          <Chip
                            label={session.warning || 'Suspicious activity'}
                            color="error"
                          />
                        ) : (
                          <Chip label="Normal" color="success" />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default AdminPortal;
